/**
 * EmbedShell — bare-chrome wrapper for iframe embeds.
 *
 * Responsibilities:
 * - Read `?theme=dark|light` and `?compact=1` from URL, set matching classes.
 * - Load brand fonts (Cormorant Garamond + Inter) via Google Fonts.
 * - Post height changes to window.parent via `{ type: "forgeos:resize", height }`.
 * - Render a small "Powered by ForgeOS" watermark and a "Try the full platform →" CTA.
 * - Initialize GA4 if REACT_APP_GA4_ID is set.
 */
import React, { useEffect, useRef } from "react";
import { initGA4, gaEvent } from "@/lib/analytics";

const BRAND_FONT_HREF =
    "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Inter:wght@300;400;500;600&display=swap";

/**
 * Origins allowed to embed / interact with ForgeOS embed pages.
 * The child (this shell) sends postMessage with targetOrigin "*" — real security
 * comes from the PARENT validating event.origin against this list. Exported here
 * so the marketing site's auto-resize snippet (see marketing_integration_plan.md)
 * uses the same source of truth.
 */
export const EMBED_ALLOWED_PARENT_ORIGINS = [
    "https://app.thedreamforgecompany.com",
    "https://thedreamforgecompany.com",
    "https://www.thedreamforgecompany.com",
    "https://construct-core-13.preview.emergentagent.com",
];

function useQueryParam(name, def = "") {
    if (typeof window === "undefined") return def;
    return new URLSearchParams(window.location.search).get(name) || def;
}

/**
 * Emit height on every content mutation + resize. Debounced.
 */
function usePostHeight(rootRef) {
    const lastRef = useRef(0);
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        let raf = 0;
        const post = () => {
            const h = Math.ceil(el.getBoundingClientRect().height);
            if (Math.abs(h - lastRef.current) < 2) return;
            lastRef.current = h;
            try {
                window.parent?.postMessage({ type: "forgeos:resize", height: h, source: window.location.pathname }, "*");
            } catch (_e) { /* ignore */ }
        };
        const schedule = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => { raf = 0; post(); });
        };
        // Initial post
        post();
        // ResizeObserver on root + document body
        const ro = new ResizeObserver(schedule);
        ro.observe(el);
        // Mutation observer catches DOM changes that don't affect element size directly
        const mo = new MutationObserver(schedule);
        mo.observe(el, { childList: true, subtree: true, characterData: true, attributes: true });
        window.addEventListener("resize", schedule);
        // periodic guard in case ResizeObserver misses layouts inside charts / recharts
        const iv = setInterval(schedule, 1500);
        return () => {
            ro.disconnect(); mo.disconnect();
            window.removeEventListener("resize", schedule);
            clearInterval(iv);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [rootRef]);
}

export default function EmbedShell({ children, kind }) {
    const rootRef = useRef(null);
    const theme = useQueryParam("theme", "dark") === "light" ? "light" : "dark";
    const compact = useQueryParam("compact", "") === "1";

    useEffect(() => {
        // Load brand font
        if (!document.getElementById("forgeos-embed-fonts")) {
            const link = document.createElement("link");
            link.id = "forgeos-embed-fonts";
            link.rel = "stylesheet";
            link.href = BRAND_FONT_HREF;
            document.head.appendChild(link);
        }
        // Load Inter class via body
        document.body.classList.add("forgeos-embed-body");
        document.body.style.margin = "0";
        document.body.style.background = "transparent";

        initGA4();
        gaEvent("embed_view", { embed_kind: kind });
    }, [kind]);

    usePostHeight(rootRef);

    // Announce ready to parent
    useEffect(() => {
        try { window.parent?.postMessage({ type: "forgeos:ready", kind }, "*"); } catch (_e) { /* ignore */ }
    }, [kind]);

    const bg = theme === "dark" ? "#0F2747" : "#F7F5EE";
    const fg = theme === "dark" ? "#EEF3F8" : "#0F2747";
    const accent = "#DCEEFF";

    // Where "Try the full platform" should send the visitor. Prefer env-configured
    // app domain (prod), fall back to same-origin (preview) so the CTA works everywhere.
    const appDomain = (process.env.REACT_APP_APP_DOMAIN || "").replace(/\/+$/, "");
    const ctaBase = appDomain || (typeof window !== "undefined" ? window.location.origin : "");
    const ctaHref = `${ctaBase}/register?utm_source=embed&utm_medium=${encodeURIComponent(kind)}&utm_campaign=embed_cta`;

    return (
        <div
            ref={rootRef}
            className={`forgeos-embed forgeos-embed--${theme} ${compact ? "forgeos-embed--compact" : ""}`}
            style={{
                background: bg,
                color: fg,
                fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, sans-serif",
                minHeight: 320,
                position: "relative",
                overflow: "hidden",
            }}
            data-testid={`embed-${kind}`}
        >
            <style>{`
                .forgeos-embed h1, .forgeos-embed h2, .forgeos-embed h3, .forgeos-embed h4,
                .forgeos-embed .font-display {
                    font-family: 'Cormorant Garamond', 'Georgia', serif;
                    font-weight: 500;
                    letter-spacing: -0.01em;
                }
                .forgeos-embed .font-display-italic { font-style: italic; }
                .forgeos-embed a { color: ${accent}; }
                .forgeos-embed--compact .section { padding-top: 1.5rem !important; padding-bottom: 1.5rem !important; }
                .forgeos-embed .glass {
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(220,238,255,0.18);
                    backdrop-filter: blur(8px);
                }
                .forgeos-embed--light .glass {
                    background: rgba(15,39,71,0.04);
                    border: 1px solid rgba(15,39,71,0.14);
                }
                .forgeos-embed input, .forgeos-embed textarea, .forgeos-embed select {
                    color: ${fg};
                }
                .forgeos-embed input::placeholder { color: ${theme === "dark" ? "rgba(238,243,248,0.35)" : "rgba(15,39,71,0.4)"}; }
                .forgeos-embed .container-editorial {
                    max-width: 1120px;
                    margin: 0 auto;
                    padding: 0 1.5rem;
                }
                .forgeos-embed .section {
                    padding: ${compact ? "2rem" : "3.5rem"} 0;
                }
            `}</style>

            <div style={{ paddingBottom: 80 }}>
                {children}
            </div>

            {/* Watermark + CTA */}
            <div
                style={{
                    position: "sticky",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 16px",
                    fontSize: 11,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    background: theme === "dark" ? "rgba(15,39,71,0.85)" : "rgba(247,245,238,0.9)",
                    borderTop: `1px solid ${theme === "dark" ? "rgba(220,238,255,0.14)" : "rgba(15,39,71,0.12)"}`,
                    backdropFilter: "blur(8px)",
                }}
                data-testid="embed-watermark-bar"
            >
                <span style={{ opacity: 0.65 }}>Powered by <b style={{ letterSpacing: "0.12em" }}>ForgeOS</b></span>
                <a
                    href={ctaHref}
                    target="_top"
                    rel="noopener"
                    onClick={() => gaEvent("embed_cta_click", { embed_kind: kind })}
                    style={{
                        color: fg,
                        textDecoration: "none",
                        border: `1px solid ${accent}`,
                        padding: "6px 12px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                    data-testid="embed-cta"
                >
                    Try the full platform
                    <span aria-hidden="true">→</span>
                </a>
            </div>
        </div>
    );
}
